import type { TaskPriority, TaskStatus } from "@estelle/domain";

/**
 * The single source of truth for what a status or priority is called.
 *
 * `fields.ts` builds its `FieldOption` lists from these, and `TaskDetail`
 * builds its edit controls and its type guards from the same arrays — one
 * list per enum rather than three, so a status renamed here cannot drift
 * out of sync with what the filter builder offers.
 */
export const TASK_STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "blocked", label: "Blocked" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
];

export const TASK_PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

/**
 * Narrows a `Select`'s `onValueChange` string without an `as`: the value can
 * only ever be one of the options rendered into it, but the DOM does not
 * know that, so this is what turns "a string was picked" back into "a
 * `TaskStatus` was picked" before it reaches `TasksRepository.update`.
 */
export function isTaskStatus(value: string): value is TaskStatus {
  return TASK_STATUS_OPTIONS.some((option) => option.value === value);
}

export function isTaskPriority(value: string): value is TaskPriority {
  return TASK_PRIORITY_OPTIONS.some((option) => option.value === value);
}

export function statusLabel(status: TaskStatus): string {
  return (
    TASK_STATUS_OPTIONS.find((option) => option.value === status)?.label ??
    status
  );
}

export function priorityLabel(priority: TaskPriority): string {
  return (
    TASK_PRIORITY_OPTIONS.find((option) => option.value === priority)?.label ??
    priority
  );
}
